import React, { useState, useEffect, useCallback } from "react";
import { Icons } from "../../components/common/Icons";
import { useEmployeeData } from "../../context/EmployeeContext";
import { useSocket } from "../../context/SocketContext";
import api from "../../services/api";
import "../../assets/styles/Employee/EmpAssetsPage.css";

const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "—"; } catch { return "—"; }};
const fmtMoney = (v) => `Rs.${Number(v||0).toLocaleString("en-IN",{minimumFractionDigits:2})}`;

// ── PDF Download for Lost Notice ──
async function downloadNoticePDF(notice) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation:"portrait", unit:"mm", format:"a4" });

  const pw = doc.internal.pageSize.getWidth();
  let y = 0;

  // Dark header bar
  doc.setFillColor(30, 27, 75);
  doc.rect(0, 0, pw, 28, "F");
  doc.setFont("helvetica","bold");
  doc.setFontSize(16);
  doc.setTextColor(255,255,255);
  doc.text("LOST ASSET NOTICE", pw/2, 12, { align:"center" });
  doc.setFontSize(9);
  doc.setFont("helvetica","normal");
  doc.text("This is an official notice issued by HR", pw/2, 19, { align:"center" });
  doc.text(`Ref: ${notice.report_id||notice.id||"—"}`, pw/2, 24, { align:"center" });
  y = 36;

  // Alert banner
  doc.setFillColor(254,242,242);
  doc.setDrawColor(220,38,38);
  doc.roundedRect(14, y, pw-28, 14, 3, 3, "FD");
  doc.setFont("helvetica","bold");
  doc.setFontSize(11);
  doc.setTextColor(185,28,28);
  doc.text("⚠  ACTION REQUIRED — Please acknowledge this notice", pw/2, y+9, { align:"center" });
  y += 22;

  // Asset details section
  doc.setFont("helvetica","bold");
  doc.setFontSize(10);
  doc.setTextColor(30,41,59);
  doc.text("ASSET DETAILS", 14, y);
  y += 4;
  doc.setDrawColor(220,38,38);
  doc.setLineWidth(0.5);
  doc.line(14, y, pw-14, y);
  y += 6;

  const fine = Number(notice.fine_amount||0);
  const rows = [
    ["Asset Name",    notice.asset_name || notice.asset_id || "—"],
    ["Asset ID",      notice.asset_id   || "—"],
    ["Asset Tag",     notice.asset_tag  || "—"],
    ["Asset Value",   fmtMoney(notice.asset_value)],
    ["Fine Amount",   fine > 0 ? fmtMoney(fine) : "Nil"],
    ["Lost Date",     fmtDate(notice.lost_date)],
    ["Notice Date",   fmtDate(notice.issued_date || notice.sent_at)],
    ["Received On",   fmtDate(notice.sent_at)],
  ];

  rows.forEach(([label, value], i) => {
    const rowY = y + i * 10;
    doc.setFillColor(i % 2 === 0 ? 248 : 255, i % 2 === 0 ? 250 : 255, i % 2 === 0 ? 252 : 255);
    doc.rect(14, rowY-5, pw-28, 10, "F");
    doc.setFont("helvetica","bold");
    doc.setFontSize(9);
    doc.setTextColor(100,116,139);
    doc.text(label, 18, rowY);
    doc.setFont("helvetica","normal");
    doc.setTextColor(15,23,42);
    doc.text(String(value), 70, rowY);
  });
  y += rows.length * 10 + 8;

  // HR Message
  if (notice.hr_comment) {
    doc.setFont("helvetica","bold");
    doc.setFontSize(10);
    doc.setTextColor(30,41,59);
    doc.text("HR MESSAGE", 14, y);
    y += 4;
    doc.setDrawColor(220,38,38);
    doc.line(14, y, pw-14, y);
    y += 6;
    doc.setFillColor(254,242,242);
    const msgLines = doc.splitTextToSize(notice.hr_comment, pw-40);
    const msgH = msgLines.length * 6 + 10;
    doc.roundedRect(14, y, pw-28, msgH, 3, 3, "F");
    doc.setFont("helvetica","normal");
    doc.setFontSize(9);
    doc.setTextColor(60,10,10);
    doc.text(msgLines, 20, y+7);
    y += msgH + 8;
  }

  // PM Comment
  if (notice.pm_comment) {
    doc.setFont("helvetica","bold");
    doc.setFontSize(10);
    doc.setTextColor(30,41,59);
    doc.text("MANAGER NOTE", 14, y);
    y += 4;
    doc.setDrawColor(99,102,241);
    doc.line(14, y, pw-14, y);
    y += 6;
    doc.setFillColor(239,246,255);
    const pmLines = doc.splitTextToSize(notice.pm_comment, pw-40);
    const pmH = pmLines.length * 6 + 10;
    doc.roundedRect(14, y, pw-28, pmH, 3, 3, "F");
    doc.setFont("helvetica","normal");
    doc.setFontSize(9);
    doc.setTextColor(30,64,175);
    doc.text(pmLines, 20, y+7);
    y += pmH + 10;
  }

  // Fine warning box
  if (fine > 0) {
    doc.setFillColor(255,247,237);
    doc.setDrawColor(249,115,22);
    doc.setLineWidth(1);
    doc.roundedRect(14, y, pw-28, 20, 3, 3, "FD");
    doc.setFont("helvetica","bold");
    doc.setFontSize(11);
    doc.setTextColor(194,65,12);
    doc.text(`Total Fine Imposed: ${fmtMoney(fine)}`, pw/2, y+8, { align:"center" });
    doc.setFont("helvetica","normal");
    doc.setFontSize(8);
    doc.setTextColor(154,52,18);
    doc.text("Please contact HR to discuss the fine amount and payment process.", pw/2, y+15, { align:"center" });
    y += 28;
  }

  // Acknowledgement box
  doc.setFillColor(240,253,244);
  doc.setDrawColor(187,247,208);
  doc.setLineWidth(0.5);
  doc.roundedRect(14, y, pw-28, 28, 3, 3, "FD");
  doc.setFont("helvetica","bold");
  doc.setFontSize(9);
  doc.setTextColor(22,101,52);
  doc.text("EMPLOYEE ACKNOWLEDGEMENT", 20, y+8);
  doc.setFont("helvetica","normal");
  doc.setTextColor(20,83,45);
  doc.text("I acknowledge receipt of this Lost Asset Notice and understand the details stated above.", 20, y+15);
  doc.text("Signature: ___________________________     Date: ___________________", 20, y+23);
  y += 36;

  // Footer
  doc.setFillColor(30,27,75);
  const footerY = doc.internal.pageSize.getHeight() - 14;
  doc.rect(0, footerY, pw, 14, "F");
  doc.setFont("helvetica","normal");
  doc.setFontSize(8);
  doc.setTextColor(148,163,184);
  doc.text(`Generated on ${new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"})}`, pw/2, footerY+6, { align:"center" });
  doc.text("This is a system-generated notice. Please retain for your records.", pw/2, footerY+11, { align:"center" });

  const fname = `Lost_Asset_Notice_${notice.report_id||notice.id||"notice"}_${new Date().toISOString().slice(0,10)}.pdf`;
  doc.save(fname);
}

function LostNoticeCard({ notice, onAck }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try { await downloadNoticePDF(notice); }
    catch(e) { alert("PDF generation failed: " + e.message); }
    setDownloading(false);
  };

  return (
    <div style={{
      background: notice.acknowledged ? "#f0fdf4" : "#fff1f2",
      border: `2px solid ${notice.acknowledged ? "#bbf7d0" : "#fecaca"}`,
      borderRadius: 12, padding: "14px 16px", marginBottom: 10,
      position: "relative"
    }}>
      {!notice.acknowledged && (
        <div style={{position:"absolute",top:10,right:12}}>
          <span style={{background:"#dc2626",color:"#fff",borderRadius:20,padding:"2px 8px",fontSize:10,fontWeight:700}}>
            ⚠️ ACTION NEEDED
          </span>
        </div>
      )}
      <div style={{fontWeight:700,fontSize:14,color:"#dc2626",marginBottom:8}}>
        🔴 Lost Asset Notice — {notice.asset_name||notice.asset_id}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
        {[
          ["Asset ID",       notice.asset_id||"—"],
          ["Asset Tag",      notice.asset_tag||"—"],
          ["Issued Date",    fmtDate(notice.issued_date)],
          ["Lost Date",      fmtDate(notice.lost_date)],
          ["Asset Value",    fmtMoney(notice.asset_value)],
          ["Fine Amount",    notice.fine_amount > 0
            ? <b style={{color:"#dc2626"}}>{fmtMoney(notice.fine_amount)}</b>
            : <span style={{color:"#94a3b8"}}>Nil</span>],
        ].map(([l,v])=>(
          <div key={l} style={{background:"rgba(255,255,255,.6)",borderRadius:7,padding:"6px 10px"}}>
            <div style={{fontSize:10,color:"#94a3b8",fontWeight:600,marginBottom:2}}>{l}</div>
            <div style={{fontSize:13,fontWeight:700,color:"#0f172a"}}>{v}</div>
          </div>
        ))}
      </div>
      {notice.hr_comment && (
        <div style={{background:"rgba(255,255,255,.8)",borderRadius:7,padding:"8px 10px",marginBottom:10,fontSize:12,color:"#374151"}}>
          <b style={{color:"#374151"}}>HR Message:</b> {notice.hr_comment}
        </div>
      )}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
        <div style={{fontSize:11,color:"#64748b"}}>
          Received: {fmtDate(notice.sent_at)}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {/* Download PDF button — always visible */}
          <button onClick={handleDownload} disabled={downloading}
            style={{padding:"6px 14px",background:"#1e1b4b",color:"#fff",border:"none",
              borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",
              opacity:downloading?0.6:1,display:"flex",alignItems:"center",gap:5}}>
            {downloading ? "Generating…" : "⬇ Download Notice PDF"}
          </button>
          {!notice.acknowledged && (
            <button onClick={() => onAck(notice.id)}
              style={{padding:"6px 14px",background:"#16a34a",color:"#fff",border:"none",
                borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>
              ✓ Acknowledge
            </button>
          )}
          {notice.acknowledged && (
            <span style={{fontSize:11,color:"#16a34a",fontWeight:600}}>✅ Acknowledged</span>
          )}
        </div>
      </div>
    </div>
  );
}

function EmpAssetsPage() {
  const { empAssets, empSoftware, empAITools, empCredentials, refreshAssets } = useEmployeeData();
  const [lostNotices, setLostNotices] = useState([]);
  const [assetToast, setAssetToast] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const { on, off } = useSocket();

  const loadNotices = useCallback(() => {
    api.getMyLostNotices().then(d => setLostNotices(Array.isArray(d) ? d : [])).catch(()=>{});
  }, []);

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  // Reload when HR sends a notice in real-time
  useEffect(() => {
    const handler = () => loadNotices();
    on('lost_asset:notice', handler);
    on('notification', handler);
    return () => {
      off('lost_asset:notice', handler);
      off('notification', handler);
    };
  }, [on, off, loadNotices]);

  // Listen for asset:assigned or credential:assigned — auto-refresh + toast
  useEffect(() => {
    const handler = (data) => {
      setAssetToast(data?.message || "💻 A new asset has been assigned to you!");
      setTimeout(() => setAssetToast(""), 6000);
      if (refreshAssets) refreshAssets();
    };
    const credHandler = (data) => {
      setAssetToast(data?.message || "🔐 Your system credentials have been configured!");
      setTimeout(() => setAssetToast(""), 6000);
      if (refreshAssets) refreshAssets();
    };
    on('asset:assigned', handler);
    on('credential:assigned', credHandler);
    return () => { off('asset:assigned', handler); off('credential:assigned', credHandler); };
  }, [on, off, refreshAssets]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    if (refreshAssets) await refreshAssets();
    setRefreshing(false);
  };

  const ackNotice = async (id) => {
    try {
      await api.acknowledgeLostNotice(id);
      setLostNotices(prev => prev.map(n => n.id === id ? {...n, acknowledged:1} : n));
    } catch(e) { alert("Error: " + e.message); }
  };

  const downloadLostCSV = () => {
    const headers = ["Report ID","Asset ID","Asset Name","Asset Tag","Issued Date","Lost Date","Asset Value (Rs.)","Fine Amount (Rs.)","PM Comment","HR Message","Received Date"];
    const lines = [headers.join(",")];
    lostNotices.forEach(n => {
      lines.push([
        n.report_id||"", n.asset_id||"",
        `"${(n.asset_name||"").replace(/"/g,'""')}"`,
        n.asset_tag||"",
        fmtDate(n.issued_date), fmtDate(n.lost_date),
        n.asset_value||0, n.fine_amount||0,
        `"${(n.pm_comment||"").replace(/"/g,'""')}"`,
        `"${(n.hr_comment||"").replace(/"/g,'""')}"`,
        fmtDate(n.sent_at)
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], {type:"text/csv;charset=utf-8;"});
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "my_lost_asset_notices.csv"; a.click(); URL.revokeObjectURL(a.href);
  };

  const pendingNotices = lostNotices.filter(n => !n.acknowledged);

  return (
    <div>
      {/* Asset Assigned Toast */}
      {assetToast && (
        <div style={{
          background:"linear-gradient(135deg,#1e1b4b,#4338ca)",
          borderRadius:10, padding:"12px 18px", marginBottom:16,
          display:"flex", alignItems:"center", gap:12, color:"#fff",
          boxShadow:"0 4px 16px rgba(67,56,202,.35)", animation:"slideIn .3s ease"
        }}>
          <span style={{fontSize:20}}>💻</span>
          <span style={{fontWeight:700, fontSize:14}}>{assetToast}</span>
          <button onClick={()=>setAssetToast("")}
            style={{marginLeft:"auto",background:"rgba(255,255,255,.2)",border:"none",
              borderRadius:6,color:"#fff",padding:"4px 10px",fontWeight:700,cursor:"pointer",fontSize:12}}>
            ✕
          </button>
        </div>
      )}

      {/* Lost Asset Notices */}
      {lostNotices.length > 0 && (
        <div className="card animate-in mb-24" style={{
          border: pendingNotices.length > 0 ? "2px solid #fecaca" : "1px solid #e2e8f0"
        }}>
          <div className="card-header" style={{background: pendingNotices.length > 0 ? "linear-gradient(135deg,#1e1b4b,#dc2626)" : "#f8fafc"}}>
            <div className="card-title" style={{color: pendingNotices.length > 0 ? "#fff" : "#0f172a"}}>
              🔴 Lost Asset Notices {pendingNotices.length > 0 && <span style={{marginLeft:6,background:"rgba(255,255,255,.3)",borderRadius:20,padding:"1px 8px",fontSize:12}}>{pendingNotices.length} pending</span>}
            </div>
            <button onClick={downloadLostCSV}
              style={{padding:"5px 12px",background:"rgba(255,255,255,.2)",border:"1px solid rgba(255,255,255,.3)",
                borderRadius:7,color: pendingNotices.length>0?"#fff":"#374151",fontWeight:700,fontSize:11,cursor:"pointer"}}>
              ⬇ Download CSV
            </button>
          </div>
          <div className="card-body">
            {lostNotices.map(n => (
              <LostNoticeCard key={n.id} notice={n} onAck={ackNotice} />
            ))}
          </div>
        </div>
      )}

      {/* Hardware Assets */}
      <div className="card animate-in mb-24">
        <div className="card-header">
          <div className="card-title">Hardware Assets</div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div className="card-subtitle">{empAssets.length} items</div>
            <button onClick={handleManualRefresh} disabled={refreshing}
              style={{padding:"4px 12px",background:"#f1f5f9",border:"1px solid #e2e8f0",
                borderRadius:7,fontSize:12,fontWeight:700,color:"#475569",cursor:"pointer",
                opacity: refreshing ? .5 : 1}}>
              {refreshing ? "⏳" : "↻"} Refresh
            </button>
          </div>
        </div>
        <div className="card-body">
          {empAssets.length === 0
            ? <div className="empassetspage-inline-1">No hardware assets assigned</div>
            : empAssets.map((a, i) => (
              <div className="list-item" key={i}>
                <div className="list-icon"><Icons.Monitor /></div>
                <div className="list-content">
                  <div className="list-title">{a.brand ? `${a.brand} ${a.asset_type}` : a.name || a.asset_type}</div>
                  <div className="list-sub">
                    {a.asset_id||a.id}
                    {a.serial ? ` · SN: ${a.serial}` : ""}
                    {a.asset_tag ? ` · Tag: ${a.asset_tag}` : ""}
                  </div>
                  <div style={{display:"flex",gap:8,marginTop:4,flexWrap:"wrap"}}>
                    {a.condition && (
                      <span style={{fontSize:11,background:"#eff6ff",color:"#2563eb",
                        padding:"1px 7px",borderRadius:10,fontWeight:600}}>{a.condition}</span>
                    )}
                    {a.asset_value > 0 && (
                      <span style={{fontSize:11,background:"#f0fdf4",color:"#16a34a",
                        padding:"1px 7px",borderRadius:10,fontWeight:600}}>
                        ₹{Number(a.asset_value).toLocaleString("en-IN")}
                      </span>
                    )}
                    {a.warranty_date && (
                      <span style={{fontSize:11,background:"#fef9c3",color:"#92400e",
                        padding:"1px 7px",borderRadius:10,fontWeight:600}}>
                        Warranty: {new Date(a.warranty_date).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}
                      </span>
                    )}
                    {a.issued_date && (
                      <span style={{fontSize:11,color:"#94a3b8"}}>
                        Issued: {new Date(a.issued_date).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}
                      </span>
                    )}
                  </div>
                </div>
                <span className={`status-badge ${a.status==="Assigned"?"approved":a.status==="Maintenance"?"pending":"approved"}`}>
                  <span className="sb-dot" />{a.status||"Assigned"}
                </span>
              </div>
            ))}
        </div>
      </div>

      {/* System Credentials */}
      <div className="card animate-in mb-24">
        <div className="card-header">
          <div className="card-title">System Credentials</div>
          <div className="card-subtitle">{empCredentials ? "Configured by IT Admin" : "Not yet assigned"}</div>
        </div>
        <div className="card-body">
          {!empCredentials ? (
            <div style={{textAlign:"center",padding:"24px 0",color:"#94a3b8",fontSize:13}}>
              🔐 No system credentials assigned yet
            </div>
          ) : (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {[
                ["🖥 Laptop Username", empCredentials.laptopUser],
                ["🔑 Laptop Password", empCredentials.laptopPass ? "••••••••" : "—"],
                ["🌐 IP Address",      empCredentials.ipAddress || "—"],
                ["📧 System Email",    empCredentials.email     || "—"],
                ["🔒 VPN Access",      empCredentials.vpnEnabled ? "✅ Enabled" : "❌ Disabled"],
              ].map(([label, val]) => (
                <div key={label} style={{background:"#f8fafc",borderRadius:8,padding:"10px 14px",
                  border:"1px solid #e2e8f0"}}>
                  <div style={{fontSize:11,color:"#64748b",fontWeight:600,marginBottom:3}}>{label}</div>
                  <div style={{fontSize:13,color:"#1e293b",fontWeight:600}}>{val}</div>
                </div>
              ))}
              {empCredentials.systemAccess?.length > 0 && (
                <div style={{gridColumn:"1/-1",background:"#f8fafc",borderRadius:8,padding:"10px 14px",
                  border:"1px solid #e2e8f0"}}>
                  <div style={{fontSize:11,color:"#64748b",fontWeight:600,marginBottom:6}}>🛡 System Access</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {empCredentials.systemAccess.map((s,i)=>(
                      <span key={i} style={{background:"#eff6ff",color:"#2563eb",padding:"2px 10px",
                        borderRadius:20,fontSize:12,fontWeight:600}}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid-2">
        <div className="card animate-in delay-2">
          <div className="card-header">
            <div className="card-title">Software Licenses</div>
            <div className="card-subtitle">{empSoftware.length} licenses</div>
          </div>
          <div className="card-body">
            {empSoftware.length === 0
              ? <div className="empassetspage-inline-2">No software licenses</div>
              : <div className="tag-wrap">{empSoftware.map((s,i)=><span className="tag" key={i}>{typeof s==="string"?s:s.name||s}</span>)}</div>}
          </div>
        </div>
        <div className="card animate-in delay-3">
          <div className="card-header">
            <div className="card-title">AI Tools</div>
            <div className="card-subtitle">{empAITools.length} tools</div>
          </div>
          <div className="card-body">
            {empAITools.length === 0
              ? <div className="empassetspage-inline-3">No AI tools assigned</div>
              : <div className="tag-wrap">{empAITools.map((t,i)=><span className="tag accent" key={i}>{typeof t==="string"?t:t.name||t}</span>)}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmpAssetsPage;
